import React from 'react';
import Button from '../../../../components/Button';
import { CompetitorsTableProps } from './types';

const CompetitorsTable: React.FC<CompetitorsTableProps> = ({
  competitors,
  editingCompetitor,
  editForm,
  setEditingCompetitor,
  setEditForm,
  onSaveEdit,
  onCancelEdit,
  onEdit,
  onDelete
}) => {
  if (!competitors || competitors.length === 0) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '2rem', 
        color: '#666',
        backgroundColor: '#f8f9fa',
        borderRadius: '4px'
      }}>
        <p>No competitors added yet. Click "Add Competitor" or "Find Competitors" to get started.</p>
      </div>
    );
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ 
        width: '100%', 
        borderCollapse: 'collapse',
        backgroundColor: '#fff',
        borderRadius: '8px',
        overflow: 'hidden',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
      }}>
        <thead>
          <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6' }}>
            <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 600 }}>Competitor</th>
            <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 600 }}>Address</th>
            <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 600 }}>Distance</th>
            <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 600 }}>Threat Rating</th>
            <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 600 }}>Type</th>
            <th style={{ padding: '1rem', textAlign: 'right', fontWeight: 600 }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {competitors.map((competitor, index) => {
            const threatRating = competitor.threat_rating || 3;
            return (
              <tr key={competitor.id || index} style={{ borderBottom: '1px solid #dee2e6' }}>
                {editingCompetitor === competitor.id ? (
                  <>
                    <td style={{ padding: '1rem' }}>
                      <input
                        type="text"
                        value={editForm.name || ''}
                        onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                        style={{ 
                          width: '100%', 
                          padding: '0.5rem',
                          borderRadius: '4px',
                          border: '1px solid #d9d9d9',
                          fontSize: '0.9rem'
                        }}
                        aria-label="Competitor name"
                      />
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <input
                        type="text"
                        value={editForm.address?.formatted_address || ''}
                        onChange={(e) => setEditForm({
                          ...editForm, 
                          address: {
                            ...editForm.address,
                            formatted_address: e.target.value
                          }
                        })}
                        style={{ 
                          width: '100%', 
                          padding: '0.5rem',
                          borderRadius: '4px',
                          border: '1px solid #d9d9d9',
                          fontSize: '0.9rem'
                        }}
                        aria-label="Competitor address"
                      />
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <input
                        type="number"
                        value={editForm.distance_miles || 0}
                        onChange={(e) => setEditForm({
                          ...editForm, 
                          distance_miles: parseFloat(e.target.value)
                        })}
                        style={{ 
                          width: '100%', 
                          padding: '0.5rem',
                          borderRadius: '4px',
                          border: '1px solid #d9d9d9',
                          fontSize: '0.9rem'
                        }}
                        aria-label="Distance in miles"
                      />
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <input
                        type="number"
                        min="1"
                        max="5"
                        step="1"
                        value={editForm.threat_rating || 1}
                        onChange={(e) => setEditForm({
                          ...editForm, 
                          threat_rating: parseInt(e.target.value)
                        })}
                        style={{ 
                          width: '100%', 
                          padding: '0.5rem',
                          borderRadius: '4px',
                          border: '1px solid #d9d9d9',
                          fontSize: '0.9rem'
                        }}
                        aria-label="Threat rating from 1 to 5"
                      />
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <select
                        value={editForm.competition_type || 'Potential'}
                        onChange={(e) => setEditForm({
                          ...editForm, 
                          competition_type: e.target.value as 'Direct' | 'Indirect' | 'Potential'
                        })}
                        style={{ 
                          width: '100%', 
                          padding: '0.5rem',
                          borderRadius: '4px',
                          border: '1px solid #d9d9d9',
                          fontSize: '0.9rem'
                        }}
                        aria-label="Competition type"
                      >
                        <option value="Direct">Direct</option>
                        <option value="Indirect">Indirect</option>
                        <option value="Potential">Potential</option>
                      </select>
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                        <Button 
                          onClick={onSaveEdit}
                          style={{ 
                            padding: '0.25rem 0.5rem',
                            fontSize: '0.85rem'
                          }}
                        >
                          Save
                        </Button>
                        <Button 
                          onClick={onCancelEdit}
                          style={{ 
                            padding: '0.25rem 0.5rem',
                            fontSize: '0.85rem',
                            backgroundColor: '#fff',
                            color: '#666',
                            border: '1px solid #ccc'
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </td>
                  </>
                ) : (
                  <>
                    <td style={{ padding: '1rem', fontWeight: 500 }}>{competitor.name}</td>
                    <td style={{ padding: '1rem', color: '#666' }}>
                      {competitor.address?.formatted_address || 'No address'}
                    </td>
                    <td style={{ padding: '1rem' }}>
                      {competitor.distance_miles ? `${competitor.distance_miles.toFixed(1)} mi` : 'N/A'}
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <span style={{ 
                        padding: '0.25rem 0.5rem',
                        borderRadius: '12px',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        backgroundColor: threatRating >= 4 ? '#ffebee' : 
                                       threatRating >= 3 ? '#fff8e1' : '#e8f5e8',
                        color: threatRating >= 4 ? '#c62828' : 
                               threatRating >= 3 ? '#f57c00' : '#2e7d32'
                      }}>
                        {threatRating}/5
                      </span>
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <span style={{ 
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        fontSize: '0.8rem',
                        backgroundColor: '#f0f0f0',
                        color: '#333'
                      }}>
                        {competitor.competition_type}
                      </span>
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                        <Button 
                          onClick={() => onEdit(competitor)}
                          style={{ 
                            padding: '0.25rem 0.5rem',
                            fontSize: '0.85rem',
                            backgroundColor: '#fff',
                            color: '#1890ff',
                            border: '1px solid #1890ff'
                          }}
                        >
                          Edit
                        </Button>
                        <Button 
                          onClick={() => onDelete(competitor.id)}
                          style={{ 
                            padding: '0.25rem 0.5rem',
                            fontSize: '0.85rem',
                            backgroundColor: '#fff',
                            color: '#ff4d4f',
                            border: '1px solid #ff4d4f'
                          }}
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default CompetitorsTable;
